import { useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { Heading, Stack, Text } from '@eluve/components';

type DiagnosticsData = {
  healthy: boolean;
  projects: Array<{
    id: string;
    name: string;
    tenantId: string | null;
  }>;
  serverVersion: string | null;
  error: string | null;
};

export const MedplumDiagnosticsPage: React.FC = () => {
  const apiClient = useApiClient();
  const [data, setData] = useState<DiagnosticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDiagnostics = async () => {
      try {
        const result = await apiClient.medplumAdmin.diagnostics();
        if (result.status === 200) {
          setData(result.body);
        }
      } catch {
        setData({
          healthy: false,
          projects: [],
          serverVersion: null,
          error: 'Failed to reach API',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDiagnostics();
  }, [apiClient]);

  if (loading) {
    return <Text size="md">Loading Medplum diagnostics...</Text>;
  }

  if (!data) {
    return <Text size="md">Failed to load diagnostics.</Text>;
  }

  return (
    <div className="space-y-6">
      <Heading size="4xl" weight="bold">
        Medplum Diagnostics
      </Heading>

      <div className="rounded-lg border p-4">
        <Heading size="2xl" weight="semibold" className="mb-2">
          Server Health
        </Heading>
        <Stack direction="horizontal" align="center" gap={2}>
          <span
            className={`inline-block size-3 rounded-full ${
              data.healthy
                ? 'bg-bg-positive-strong-primary'
                : 'bg-bg-negative-strong-primary'
            }`}
          />
          <Text size="md">{data.healthy ? 'Healthy' : 'Unhealthy'}</Text>
        </Stack>
        {data.error && (
          <Text size="md" className="mt-2 text-text-negative">
            Error: {data.error}
          </Text>
        )}
      </div>

      <div className="rounded-lg border p-4">
        <Heading size="2xl" weight="semibold" className="mb-4">
          Projects ({data.projects.length})
        </Heading>
        {data.projects.length === 0 ? (
          <Text size="md" color="tertiary">
            No projects found.
          </Text>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b text-text-tertiary">
                <th className="pb-2 font-medium">Name</th>
                <th className="pb-2 font-medium">Project ID</th>
                <th className="pb-2 font-medium">Tenant ID</th>
              </tr>
            </thead>
            <tbody>
              {data.projects.map((project) => (
                <tr key={project.id} className="border-b last:border-b-0">
                  <td className="py-2">{project.name}</td>
                  <td className="py-2 font-mono text-xs">{project.id}</td>
                  <td className="py-2 font-mono text-xs">
                    {project.tenantId ?? (
                      <span className="text-text-tertiary">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
